import {
  buildQueryExpression,
  buildStaticFilterValue,
  definePager,
  defineQuerySummary,
  defineResultList,
  defineResultsPerPage,
  defineSearchBox,
  defineSearchEngine,
  defineStaticFilter,
  loadQueryActions,
  type InferHydratedState,
  type InferStaticState,
  type PagerDefinition,
  type QuerySummaryDefinition,
  type ResultListDefinition,
  type ResultsPerPageDefinition,
  type SearchBoxDefinition,
  type SearchEngineDefinitionOptions,
  type StaticFilterDefinition,
} from '@coveo/headless-react/ssr';
import { buildSearchEngine, SearchEngine } from '@coveo/headless';
import { DEFAULT_LOCALE } from '@/i18n';
import { getServerEnv } from '../constants/environment';
import { generateSearchToken, getValidToken } from './token-manager';

export const NUMBER_OF_RESULTS = 12;

const searchHub = 'docs.unity.com-search';
const numberOfSuggestions = 10;

const defaultConfig: SearchEngineDefinitionOptions<{
  searchBox: SearchBoxDefinition;
  resultList: ResultListDefinition;
  pager: PagerDefinition;
  resultsPerPage: ResultsPerPageDefinition;
  querySummary: QuerySummaryDefinition;
  staticFilter: StaticFilterDefinition;
}> = {
  configuration: {
    organizationId: getServerEnv().NEXT_PUBLIC_COVEO_ORG_ID || '',
    accessToken: '', // Will be defined in the build function
    renewAccessToken: generateSearchToken,
    search: {
      searchHub,
    },
    analytics: { enabled: true },
  },
  controllers: {
    searchBox: defineSearchBox({ options: { numberOfSuggestions, clearFilters: false } }),
    resultList: defineResultList(),
    pager: definePager(),
    resultsPerPage: defineResultsPerPage({ initialState: { numberOfResults: NUMBER_OF_RESULTS } }),
    querySummary: defineQuerySummary(),
    staticFilter: defineStaticFilter({
      options: {
        id: 'search-tabs',
        values: [
          buildStaticFilterValue({
            caption: 'all',
            state: 'idle',
            expression: buildQueryExpression()
              .addStringField({
                field: 'clickableuri',
                operator: 'contains',
                values: [
                  'https://issuetracker.unity3d.com',
                  'https://support.unity.com',
                  'https://docs.unity3d.com',
                  'https://docs.unity.com',
                  'https://docs-multiplayer.unity3d.com',
                  'https://services.docs.unity.com',
                  'https://discussions.unity.com',
                  'https://learn.unity.com',
                ],
              })
              .toQuerySyntax(),
          }),
          buildStaticFilterValue({
            caption: 'documentation',
            state: 'selected',
            expression: buildQueryExpression()
              .addStringField({
                field: 'clickableuri',
                operator: 'contains',
                values: [
                  'https://docs.unity3d.com',
                  'https://docs.unity.com',
                  'https://docs-multiplayer.unity3d.com',
                  'https://services.docs.unity.com',
                ],
              })
              .toQuerySyntax(),
          }),
          buildStaticFilterValue({
            caption: 'support',
            state: 'idle',
            expression: buildQueryExpression()
              .addStringField({
                field: 'clickableuri',
                operator: 'contains',
                values: ['https://support.unity.com'],
              })
              .toQuerySyntax(),
          }),
          buildStaticFilterValue({
            caption: 'learn',
            state: 'idle',
            expression: buildQueryExpression()
              .addStringField({
                field: 'clickableuri',
                operator: 'contains',
                values: ['https://learn.unity.com'],
              })
              .toQuerySyntax(),
          }),
          buildStaticFilterValue({
            caption: 'discussions',
            state: 'idle',
            expression: buildQueryExpression()
              .addStringField({
                field: 'clickableuri',
                operator: 'contains',
                values: ['https://discussions.unity.com'],
              })
              .toQuerySyntax(),
          }),
        ],
      },
    }),
  },
  loggerOptions: {
    level: getServerEnv().NODE_ENV === 'development' ? 'warn' : 'error',
  },
};

export const engineDefinition = defineSearchEngine(defaultConfig);

export async function initializeClientEngine(locale: string = DEFAULT_LOCALE): Promise<SearchEngine> {
  const accessToken = await getValidToken();
  return buildSearchEngine({
    configuration: {
      ...defaultConfig.configuration,
      accessToken,
      search: {
        locale,
      },
    },
  });
}

export type SearchStaticState = InferStaticState<typeof engineDefinition>;
export type SearchHydratedState = InferHydratedState<typeof engineDefinition>;

async function getBuildResult(query?: string) {
  const buildResult = await engineDefinition.build({
    async extend(options) {
      return {
        ...options,
        configuration: {
          ...options.configuration,
          accessToken: options.configuration.accessToken || (await generateSearchToken()),
          renewAccessToken: generateSearchToken,
        },
      };
    },
  });
  if (query) {
    const { updateQuery } = loadQueryActions(buildResult.engine);
    buildResult.engine.dispatch(updateQuery({ q: query }));
  }

  return buildResult;
}

export async function fetchStaticState(query?: string): Promise<SearchStaticState> {
  if (query) {
    // If a query is provided, fetch full static state including results.
    return engineDefinition.fetchStaticState.fromBuildResult({
      buildResult: await getBuildResult(query),
    });
  }

  // Get controllers from the build result without executing a query.
  const { controllers } = await getBuildResult(query);

  return {
    controllers: {
      searchBox: { state: controllers.searchBox.state },
      resultList: { state: controllers.resultList.state },
      pager: { state: controllers.pager.state },
      resultsPerPage: { state: controllers.resultsPerPage.state },
      querySummary: { state: controllers.querySummary.state },
      staticFilter: { state: controllers.staticFilter.state },
    },
    // Provide a placeholder search action to avoid triggering an actual search.
    searchAction: {
      type: 'search/executeSearch/fulfilled',
    },
  };
}

export async function hydrateStaticState(
  options: { searchAction: SearchStaticState['searchAction'] },
  query?: string,
): Promise<SearchHydratedState> {
  if (query) {
    // If a query is present, hydrate the engine with full search state.
    return engineDefinition.hydrateStaticState.fromBuildResult({
      buildResult: await getBuildResult(query),
      searchAction: options.searchAction,
    });
  }

  // Otherwise, initialize the engine and controllers without search execution.
  const { engine, controllers } = await getBuildResult();

  return { engine, controllers };
}

export const { StaticStateProvider, HydratedStateProvider, setNavigatorContextProvider, useEngine } = engineDefinition;

export const { useSearchBox, useResultList, usePager, useQuerySummary, useStaticFilter, useResultsPerPage } =
  engineDefinition.controllers;
